import SearchBar from 'components/SearchBar/SearchBar';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import { useCallback, useState } from 'react';
import { Container } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useDeleteGlobalActionMutation,
  useGetGlobalActionsQuery,
  useLockGlobalActionMutation,
  useSetGlobalActionEnabledMutation,
  useUnlockGlobalActionMutation,
} from 'services/globalActions/globalActions.api';
import { formatIsoToLocalHHMM } from 'utils/dates/time.utils';
import { notifyErrorToast, notifySuccessToast } from 'utils/notifications/toast.utils';
import GlobalActionWarningModal from '../ActionBuilder/components/GlobalActionWarningModal';
import {
  actionPermissionNotice,
  readGlobalActionPermissionRefusal,
} from '../ActionBuilder/utils/actionPermissions.utils';
import Accordions from './components/Accordions';
import Table from './components/Table';

const ActionList = ({ setInitialData, setShowGlobalAction, setReadOnly }) => {
  const {
    data: globalActions = [],
    isLoading: loadingGlobalActions,
    refetch: refetchGlobalActions,
  } = useGetGlobalActionsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [unlockGlobalAction] = useUnlockGlobalActionMutation();
  const [lockGlobalAction] = useLockGlobalActionMutation();
  const [setGlobalActionEnabled] = useSetGlobalActionEnabledMutation();
  const [deleteGlobalAction] = useDeleteGlobalActionMutation();
  const [params] = useSearchParams();
  // Unified modal state
  const [warningModalOpen, setWarningModalOpen] = useState(false);
  const [warningModalItem, setWarningModalItem] = useState(null);
  const [warningModalMode, setWarningModalMode] = useState('delete');

  const getLatestGlobalAction = useCallback(
    async (id) => {
      const latestGlobalActions = await refetchGlobalActions().unwrap();
      return Array.isArray(latestGlobalActions)
        ? (latestGlobalActions.find((action) => action.id === id) ?? null)
        : null;
    },
    [refetchGlobalActions],
  );

  const enableGlobalAction = useCallback(
    async (id, value) => {
      const action = globalActions.find((action) => action.id === id);
      if (action.status === value) return action;
      // If setting offline (status false), show warning modal, but only if not already offline
      if (value === false) {
        if (!action.status) return action; // Already offline, do nothing
        setWarningModalItem(action);
        setWarningModalMode('offline');
        setWarningModalOpen(true);
        return;
      }
      try {
        await setGlobalActionEnabled({ id, status: value }).unwrap();
      } catch (error) {
        console.error(`Failed to set global action to ${value}`);
        // The backend permission middleware refuses an enable with 403 and names the missing keys.
        const missing = readGlobalActionPermissionRefusal(error);
        if (missing) {
          toast.error(actionPermissionNotice(missing), { toastId: 'global-action-permission' });
        } else {
          toast.error('The Global Action could not be enabled. Try again.');
        }
      }
    },
    [globalActions, setGlobalActionEnabled],
  );

  const onView = useCallback(
    async (globalAction) => {
      try {
        const latestGlobalAction = await getLatestGlobalAction(globalAction.id);

        if (!latestGlobalAction) {
          toast.error('Failed to load the latest Global Action. Please try again.');
          return;
        }

        setReadOnly(true); // Make's the global action builder form read-only
        setInitialData(latestGlobalAction); // Set the data of the global action to be viewed in the form
        setShowGlobalAction(true); // Displays the global action builder form
      } catch (error) {
        toast.error('Failed to load the latest Global Action. Please try again.');
      }
    },
    [getLatestGlobalAction, setReadOnly, setInitialData, setShowGlobalAction],
  );

  const onEdit = useCallback(
    async (globalAction) => {
      try {
        // Acquire an exclusive edit lock before opening the editor
        await lockGlobalAction(globalAction.id).unwrap();
      } catch (error) {
        if (error?.status === 423) {
          const tryAt = formatIsoToLocalHHMM(error?.raw?.expiresAt);
          toast.error(
            `This global action is currently being edited by another user.${tryAt ? ` Try again at ${tryAt}` : ''}`,
          );
        } else {
          toast.error(`Failed to acquire edit lock. Please try again.`);
        }
        return;
      }

      try {
        const lockedGlobalAction = await getLatestGlobalAction(globalAction.id);

        if (!lockedGlobalAction) {
          await unlockGlobalAction(globalAction.id).unwrap();
          toast.error('Failed to load the locked Global Action. Please try again.');
          return;
        }

        setReadOnly(false);
        setInitialData(lockedGlobalAction);
        setShowGlobalAction(true);
      } catch (error) {
        await unlockGlobalAction(globalAction.id).unwrap();
        toast.error('Failed to load the locked Global Action. Please try again.');
      }
    },
    [
      setReadOnly,
      setInitialData,
      setShowGlobalAction,
      lockGlobalAction,
      getLatestGlobalAction,
      unlockGlobalAction,
    ],
  );

  const onRemove = useCallback((globalAction) => {
    setWarningModalItem(globalAction);
    setWarningModalMode('delete');
    setWarningModalOpen(true);
  }, []);

  const handleWarningModalConfirm = async (confirmed) => {
    if (!confirmed || !warningModalItem) {
      setWarningModalOpen(false);
      setWarningModalItem(null);
      return;
    }
    if (warningModalMode === 'delete') {
      try {
        await deleteGlobalAction(warningModalItem.id).unwrap();
        notifySuccessToast('Global action', { isAddOperation: false, isDeleteOperation: true });
      } catch (error) {
        console.error(`Failed to delete global action`);
        notifyErrorToast('Global action', { isAddOperation: false, isDeleteOperation: true });
      }
    } else if (warningModalMode === 'offline') {
      try {
        await setGlobalActionEnabled({ id: warningModalItem.id, status: false }).unwrap();
      } catch (error) {
        console.error('Failed to set global action offline');
      }
    }
    setWarningModalOpen(false);
    setWarningModalItem(null);
  };

  const searchFilter = useCallback(
    (globalAction) => {
      if (!globalAction) return false;

      const search = params.get('search');

      if (search) {
        return (
          globalAction.title
            ?.toLowerCase()
            .replace(/\s/g, '')
            .includes(search?.toLowerCase().replace(/\s/g, '')) ||
          globalAction.actionName
            ?.toLowerCase()
            .replace(/\s/g, '')
            .includes(search?.toLowerCase().replace(/\s/g, '')) ||
          globalAction.resolutionName
            ?.toLowerCase()
            .replace(/\s/g, '')
            .includes(search?.toLowerCase().replace(/\s/g, '')) ||
          globalAction.triggerName
            ?.toLowerCase()
            .replace(/\s/g, '')
            .includes(search?.toLowerCase().replace(/\s/g, ''))
        );
      } else return true;
    },
    [params],
  );

  return (
    <Container fluid className="d-flex flex-column w-100 flex-grow-1 overflow-hidden pt-3">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center flex-shrink-0 mb-3">
        <div className="fw-bold fs-4 w-100">Global Actions</div>
        <SearchBar className="bg-primary-highlight rounded-3 max-w-3xs" />
      </div>
      {!loadingGlobalActions ? (
        <div className="flex-grow-1 overflow-hidden d-flex flex-column min-h-0">
          <div className="d-none d-lg-flex flex-column flex-grow-1 min-h-0">
            <Table
              enableGlobalAction={enableGlobalAction}
              onView={onView}
              onEdit={onEdit}
              onRemove={onRemove}
              searchFilter={searchFilter}
            />
          </div>
          <div className="d-flex d-lg-none flex-column flex-grow-1 overflow-auto">
            <Accordions
              enableGlobalAction={enableGlobalAction}
              onView={onView}
              onEdit={onEdit}
              onRemove={onRemove}
              searchFilter={searchFilter}
            />
          </div>
        </div>
      ) : (
        <WidgetLoadingAnimation />
      )}
      <GlobalActionWarningModal
        toWarn={warningModalOpen}
        globalAction={warningModalItem}
        resolveWarning={handleWarningModalConfirm}
        mode={warningModalMode}
      />
    </Container>
  );
};

export default ActionList;
